import type { GetStaticPaths, GetStaticProps } from "next";
import dynamic from "next/dynamic";
import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import JsonLd from "@/components/JsonLd";
import { SITE_URL, templateHowToLd } from "@/lib/structuredData";
import { toPublicMap, type PublicMap } from "@/funnel/publicMap";
import { termsForTemplate } from "@/lib/glossary";
import {
  getTemplate,
  relatedTemplates,
  TEMPLATES,
  type TemplateDef,
} from "@/templates";

type RelatedCard = Pick<TemplateDef, "slug" | "name" | "tagline" | "category">;

// ReactFlow measures the DOM — client-only, like the app mapper.
const PublicMapViewer = dynamic(() => import("@/funnel/PublicMapViewer"), {
  ssr: false,
  loading: () => (
    <div className="flex h-full items-center justify-center text-[13px] text-slate-400">
      Loading map…
    </div>
  ),
});

type TermCard = { slug: string; term: string; definition: string };

type Props = {
  template: Omit<TemplateDef, "flow">;
  map: PublicMap;
  related: RelatedCard[];
  /** Glossary terms this template demonstrates — derived from the glossary
      registry, so the interlinking is bidirectional and never dangles. */
  terms: TermCard[];
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: TEMPLATES.map((t) => ({ params: { slug: t.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = ({ params }) => {
  const t = getTemplate(String(params?.slug ?? ""));
  if (!t) return { notFound: true };
  const { flow, ...rest } = t;
  return {
    props: {
      template: rest,
      related: relatedTemplates(t.slug).map(({ slug, name, tagline, category }) => ({
        slug,
        name,
        tagline,
        category,
      })),
      terms: termsForTemplate(t.slug).map(({ slug, term, definition }) => ({
        slug,
        term,
        definition,
      })),
      // Same sanitized shape the share pages use — templates have no
      // automation internals anyway, but one code path keeps it honest.
      map: toPublicMap({
        id: t.slug,
        name: t.name,
        createdAt: "",
        updatedAt: "",
        flow,
      }),
    },
  };
};

export default function TemplatePage({ template, map, related, terms }: Props) {
  const pageUrl = `${SITE_URL}/templates/${template.slug}`;
  const ogImage = `${SITE_URL}/og/template/${template.slug}.png`;
  const cloneHref = `${APP_URL}/signup?template=${template.slug}`;
  const signInHref = `${APP_URL}/login?callbackUrl=${encodeURIComponent(
    `/?template=${template.slug}`
  )}`;

  return (
    <SiteShell
      title={`${template.name} template — FrazzleCRM`}
      description={template.description}
      canonical={pageUrl}
      ogTitle={`${template.name} — free funnel template`}
      ogType="article"
      /* Per-template card: this template's actual map silhouette. */
      ogImage={ogImage}
      ogImageAlt={`The ${template.name} funnel map`}
    >
      {/* HowTo schema mirrors the "How cloning works" steps shown below. */}
      <JsonLd data={templateHowToLd(template.name)} />

      <section className="mx-auto max-w-6xl px-6 pt-10">
        <Link
          href="/templates"
          className="text-[12.5px] font-medium text-slate-400 hover:text-slate-600"
        >
          ← All templates
        </Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <h1 className="text-[30px] font-extrabold tracking-tight text-slate-900 sm:text-[36px]">
              {template.name}
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
              {template.tagline}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={cloneHref}
              className="rounded-xl bg-indigo-600 px-5 py-2.5 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
            >
              Use this template — free
            </a>
            <a
              href={signInHref}
              className="text-[13px] font-medium text-slate-500 hover:text-slate-700"
            >
              or sign in
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-6 max-w-6xl px-6">
        <div className="h-[520px] overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
          <PublicMapViewer map={map} />
        </div>
        <p className="mt-2 text-center text-[12px] text-slate-400">
          Live preview — pan and zoom. Cloning gives you this exact map,
          editable.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-[19px] font-bold text-slate-900">
              What&apos;s inside
            </h2>
            <ul className="mt-4 space-y-2.5 text-[14px] text-slate-600">
              {template.whatsInside.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-0.5 text-emerald-500">✓</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-6">
            <h2 className="text-[19px] font-bold text-slate-900">
              How cloning works
            </h2>
            <ol className="mt-4 list-decimal space-y-2.5 pl-5 text-[14px] text-slate-600">
              <li>Create a free account (no credit card).</li>
              <li>
                The template lands on your canvas as a real map — every step,
                wait and branch already wired.
              </li>
              <li>
                Swap in your copy and senders, then flip the flow live. The
                map IS the automation.
              </li>
            </ol>
            <a
              href={cloneHref}
              className="mt-6 inline-block rounded-xl bg-indigo-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-indigo-500"
            >
              Clone it now
            </a>
          </div>
        </div>
      </section>

      {terms.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 pb-12">
          <h2 className="text-[19px] font-bold text-slate-900">
            Terms worth knowing before you edit this
          </h2>
          <p className="mt-1 text-[13.5px] text-slate-500">
            Plain-English definitions of the moving parts in this map.
          </p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {terms.map((t) => (
              <Link
                key={t.slug}
                href={`/learn/${t.slug}`}
                className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
              >
                <h3 className="text-[15px] font-bold text-slate-900 group-hover:text-indigo-700">
                  {t.term}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                  {t.definition}
                </p>
              </Link>
            ))}
          </div>
          <p className="mt-5 text-[13px] text-slate-500">
            Or browse the whole{" "}
            <Link
              href="/learn"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              automation glossary
            </Link>
            .
          </p>
        </section>
      )}

      {related.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 pb-20">
          <h2 className="text-[19px] font-bold text-slate-900">
            Templates that pair with this one
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                href={`/templates/${r.slug}`}
                className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {r.category}
                </span>
                <h3 className="mt-1.5 text-[15.5px] font-bold text-slate-900 group-hover:text-indigo-700">
                  {r.name}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                  {r.tagline}
                </p>
              </Link>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-slate-500">
            Or browse{" "}
            <Link
              href="/templates"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              all {TEMPLATES.length} templates
            </Link>{" "}
            — grouped by what you sell.
          </p>
        </section>
      )}
    </SiteShell>
  );
}
